// dashboard/widgets/TrendChart.js
// PatientDetailPanel's score-trend SVG chart (seizure/medication event
// markers, per-dot evaluation tooltips). Extracted from App.js (2026-08-12
// split).

import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { c, severityColor } from '../constants';
import { formatEvaluationTooltip, trendDirectionLabel } from '../utils';
import { buildFindingsNarrative } from '../buildFindingsNarrative';
import { buildTrendSegments, versionBoundaryPositions } from '../buildTrendSegments';

export default function TrendChart({ surveys, medications, seizures, sev, evaluations, vnextRuns }) {
  const { t } = useTranslation();
  // Corrected 2026-09-08: coloring the whole line/fill by the patient's
  // CURRENT attention severity (sev) repainted a full year of green
  // history red the moment a patient escalated — the same "one number
  // for everything" problem this whole redesign exists to fix, just on
  // the chart's line color instead of the score. Each dot below is now
  // colored by that specific submission's own stored severity; the line/
  // fill connecting them is deliberately neutral (doesn't claim to
  // represent any single severity) rather than picking one point's color
  // to stand in for the whole trend.
  const lineColor = c.slate;
  // The per-dot <title> below is a native SVG hover tooltip — mouse-only,
  // invisible to keyboard/touch users despite carrying the one place this
  // clinically important context (severity_band/change_state/reason/
  // findings) is surfaced at all. Dots are now also focusable, and
  // whichever one is hovered or focused drives this visible line under the
  // chart (defaulting to the latest point when nothing's active), so the
  // same information is reachable without a mouse. Found in the 2026-08-16
  // audit.
  const [activeIdx, setActiveIdx] = useState(null);

  const sorted = [...surveys]
    .filter(s => s.clinical_score != null)
    .sort((a, b) => new Date(a.submitted_at) - new Date(b.submitted_at));

  if (sorted.length < 2) return (
    <div style={{ fontSize: 12, color: c.textSoft, fontStyle: 'italic', paddingTop: 4, marginBottom: 16 }}>
      {t('patientCard.notEnoughCheckIns')}
    </div>
  );

  const W = 480, H = 175;
  const pad = { t: 16, r: 12, b: 55, l: 30 };
  const cW  = W - pad.l - pad.r;   // 438
  const cH  = H - pad.t - pad.b;   // 104
  const ay  = pad.t + cH;          // x-axis y = 120

  const tMin = new Date(sorted[0].submitted_at).getTime();
  const tMax = new Date(sorted[sorted.length - 1].submitted_at).getTime();
  const tR   = tMax - tMin || 1;

  const scores = sorted.map(s => s.clinical_score);
  const sMin   = Math.max(0,   Math.min(...scores) - 8);
  const sMax   = Math.min(100, Math.max(...scores) + 8);
  const sR     = sMax - sMin || 1;

  const xP = ts => pad.l + ((ts - tMin) / tR) * cW;
  const yP = sc => pad.t + (1 - (sc - sMin) / sR) * cH;

  // Algorithm-version discontinuity (plan §11 risk #5, addressed 2026-09-30
  // ahead of Stage 6's CHF/SRR cutover making it concretely reachable, not
  // just theoretical): a single continuous line/fill across a version
  // change would visually claim the pet's condition jumped, when only the
  // scoring method changed. Split into one path per contiguous run of the
  // same clinical_score_algorithm_version instead of one path for the
  // whole series — see buildTrendSegments.js for the (directly tested)
  // split logic and rationale.
  const segments = buildTrendSegments(sorted);
  const segPath = pts => pts.map(s => `${xP(new Date(s.submitted_at).getTime())},${yP(s.clinical_score)}`).join(' ');
  const segFill = pts => {
    const x0 = xP(new Date(pts[0].submitted_at).getTime());
    const x1 = xP(new Date(pts[pts.length - 1].submitted_at).getTime());
    return `${x0},${ay} ${segPath(pts)} ${x1},${ay}`;
  };
  const versionBoundaries = versionBoundaryPositions(segments, s => xP(new Date(s.submitted_at).getTime()));

  const grids = [0, 25, 50, 75, 100].filter(v => v >= sMin - 2 && v <= sMax + 2);
  const step  = Math.max(2, Math.ceil(sorted.length / 4));
  const xlbls = sorted.filter((_, i) => i % step === 0 || i === sorted.length - 1);

  const fmtDate = iso => {
    const d = new Date(iso);
    return d.toLocaleString(i18n.language, { month: 'short' }) + " '" + String(d.getFullYear()).slice(2);
  };

  const szEvts  = (seizures    || []).filter(e => { const t = new Date(e.occurred_at).getTime(); return t >= tMin && t <= tMax; });
  const medEvts = (medications || []).filter(e => { const t = new Date(e.start_date + 'T12:00:00').getTime(); return t >= tMin && t <= tMax; });
  const hasEvts = szEvts.length > 0 || medEvts.length > 0;

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: c.textSoft, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{t('patientCard.scoreTrend')}</div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', overflow: 'visible' }}>

        {/* Y gridlines — release-blocker #1 (docs/BUILD_STATE.md,
            2026-09-27, Bryan): no raw numeric score anywhere client-
            facing. The gridlines themselves are fine (shape/position
            only, no digits), but the axis used to label each line with
            its literal 0-100 value ({v}) — removed. The underlying score
            still positions the dots/line/fill below; it's just never
            rendered as text anywhere in this component now. */}
        {grids.map(v => (
          <line key={v} x1={pad.l} y1={yP(v)} x2={pad.l + cW} y2={yP(v)}
            stroke={c.border} strokeWidth="1"
            strokeDasharray={v === 50 ? '3,2' : undefined} />
        ))}

        {/* X-axis baseline */}
        <line x1={pad.l} y1={ay} x2={pad.l + cW} y2={ay} stroke={c.border} strokeWidth="1" />

        {/* Seizure event lines + downward carets */}
        {szEvts.map((e, i) => {
          const x = xP(new Date(e.occurred_at).getTime());
          return (
            <g key={`sz${i}`}>
              <line x1={x} y1={pad.t} x2={x} y2={ay} stroke={c.red} strokeWidth="1" strokeDasharray="2,2" opacity="0.3" />
              <polygon points={`${x},${ay + 3} ${x - 4},${ay + 11} ${x + 4},${ay + 11}`} fill={c.red} opacity="0.75" />
            </g>
          );
        })}

        {/* Medication start lines + pin markers */}
        {medEvts.map((e, i) => {
          const x = xP(new Date(e.start_date + 'T12:00:00').getTime());
          return (
            <g key={`med${i}`}>
              <line x1={x} y1={pad.t} x2={x} y2={ay} stroke={c.slate} strokeWidth="1" strokeDasharray="2,2" opacity="0.2" />
              <line x1={x} y1={ay + 15} x2={x} y2={ay + 22} stroke={c.slate} strokeWidth="1.5" opacity="0.6" />
              <circle cx={x} cy={ay + 23} r="2.5" fill={c.slate} opacity="0.55" />
            </g>
          );
        })}

        {/* Area fill + trend line, one pair per algorithm-version segment —
            see the segments/segFill comment above. A single-point segment
            has no line/fill of its own (nothing to connect yet); its dot
            still renders below. */}
        {segments.map((seg, i) => seg.points.length > 1 && (
          <g key={`seg${i}`}>
            <polygon points={segFill(seg.points)} fill={lineColor} opacity="0.07" />
            <polyline points={segPath(seg.points)} fill="none" stroke={lineColor} strokeWidth="2"
              strokeLinejoin="round" strokeLinecap="round" />
          </g>
        ))}

        {/* Algorithm-version boundary markers — a scoring-method change,
            not a data point. Deliberately not a <title>-bearing dot: this
            isn't a clinical event, just tells the reader the line's units
            changed here so a jump isn't misread as the pet's condition
            suddenly shifting. */}
        {versionBoundaries.map((x, i) => (
          <g key={`vb${i}`}>
            <line x1={x} y1={pad.t} x2={x} y2={ay} stroke={c.textSoft} strokeWidth="1" strokeDasharray="1,3" opacity="0.5" />
            <text x={x} y={pad.t - 4} textAnchor="middle" fontSize="7" fill={c.textSoft}
              style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>{t('patientCard.scoringMethodUpdated')}</text>
          </g>
        ))}

        {/* Survey dots — <title> gives a native mouse-hover tooltip with
            severity_band/change_state/reason/findings from
            survey_evaluations, when a row exists for that survey. Also
            focusable so the same info reaches keyboard users, via the
            visible detail line below the chart (activeIdx). */}
        {sorted.map((s, i) => {
          const run = vnextRuns?.get(s.id);
          const findingsLines = buildFindingsNarrative(run?.findings, run?.clusters);
          const tooltip = formatEvaluationTooltip(
            fmtDate(s.submitted_at),
            s.clinical_score_delta != null ? trendDirectionLabel(s.clinical_score_delta, t) : null,
            evaluations?.get(s.id),
            findingsLines,
          );
          return (
            <circle key={i}
              cx={xP(new Date(s.submitted_at).getTime())}
              cy={yP(s.clinical_score)}
              r={activeIdx === i ? 4.5 : 3}
              fill={c.white} stroke={severityColor(s.severity ?? sev)} strokeWidth="1.5"
              tabIndex={0}
              role="img"
              aria-label={tooltip}
              onMouseEnter={() => setActiveIdx(i)}
              onMouseLeave={() => setActiveIdx(null)}
              onFocus={() => setActiveIdx(i)}
              onBlur={() => setActiveIdx(null)}
              style={{ cursor: 'pointer' }}
            >
              <title>{tooltip}</title>
            </circle>
          );
        })}

        {/* X-axis date labels */}
        {xlbls.map((s, i) => (
          <text key={i}
            x={xP(new Date(s.submitted_at).getTime())}
            y={ay + 33} textAnchor="middle" fontSize="8" fill={c.textSoft}
            style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
            {fmtDate(s.submitted_at)}
          </text>
        ))}

        {/* Event legend */}
        {hasEvts && (
          <g transform={`translate(${pad.l}, ${H - 5})`}>
            {szEvts.length > 0 && (
              <g>
                <polygon points="4,0 0,8 8,8" fill={c.red} opacity="0.75" />
                <text x="12" y="7" fontSize="8" fill={c.textSoft}
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>{t('patientCard.seizureLegend')}</text>
              </g>
            )}
            {medEvts.length > 0 && (
              <g transform={`translate(${szEvts.length > 0 ? 60 : 0}, 0)`}>
                <line x1="4" y1="0" x2="4" y2="7" stroke={c.slate} strokeWidth="1.5" opacity="0.6" />
                <circle cx="4" cy="9" r="2" fill={c.slate} opacity="0.55" />
                <text x="12" y="7" fontSize="8" fill={c.textSoft}
                  style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>{t('patientCard.medicationStartLegend')}</text>
              </g>
            )}
          </g>
        )}
      </svg>
      {/* Visible counterpart to the dots' hover/focus tooltip — defaults to
          the latest check-in when nothing's actively hovered/focused, so
          there's always something shown, not just on interaction. */}
      <div style={{ fontSize: 11, color: c.textSoft, marginTop: 4, whiteSpace: 'pre-line' }}>
        {(() => {
          const active = sorted[activeIdx ?? sorted.length - 1];
          const run = vnextRuns?.get(active.id);
          return formatEvaluationTooltip(
            fmtDate(active.submitted_at),
            active.clinical_score_delta != null ? trendDirectionLabel(active.clinical_score_delta, t) : null,
            evaluations?.get(active.id),
            buildFindingsNarrative(run?.findings, run?.clusters),
          );
        })()}
      </div>
    </div>
  );
}
